/**
 * @file useCertifiedOrgDirectory.ts
 * @description 名錄相關 TanStack Query hooks。
 *
 * ⚠️ 名錄已改為「發證時自動匯總」的只讀資料（客戶 PPT S6）：
 * 前端僅保留列表 / 單筆查詢；新增/編輯/刪除為 admin API 後門，無前端入口。
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { DirectoryCategory, DirectoryProcessingSite, DirectoryStatus } from '@zhcb/shared';

/** 名錄列表/詳情條目（對應 certified_org_directory 全欄 + clientName join） */
export interface DirectoryItem {
  id: number;
  category: DirectoryCategory;
  clientId: number;
  clientName: string;
  serialNo: number | null;
  registrationNo: string;
  certificateNo: string | null;
  // 受評者快照
  legalRepresentative: string | null;
  registeredAddress: string | null;
  mailingAddress: string | null;
  postalCode: string | null;
  shippingAddress: string | null;
  phone: string | null;
  // 驗證資訊
  verificationStandard: string | null;
  verificationBehavior: string | null;
  verificationType: string | null;
  verificationOption: string | null;
  verificationScope: string | null;
  verificationItems: unknown;
  organicStage: string | null;
  area: string | null;
  memberCount: number | null;
  completedYears: string | null;
  completedTypes: string | null;
  // 發證/期限
  firstIssuedAt: string | null;
  latestIssuedAt: string | null;
  validFrom: string | null;
  validUntil: string | null;
  effectiveFrom: string | null;
  daysSinceLastIssue: number | null;
  // 進度
  currentYear: string | null;
  currentStatus: string | null;
  verificationActionNote: string | null;
  // 派案
  auditorY1: string | null;
  auditorY2: string | null;
  auditorY3: string | null;
  auditorY4: string | null;
  auditorYearBase: number | null;
  plannedAuditorNext: string | null;
  auditorAvoidance: string | null;
  dispatchNote: string | null;
  rotationWarning: boolean;
  // 業務備註
  channels: string | null;
  counselor: string | null;
  primaryLandNo: string | null;
  mainParcelAddress: string | null;
  processingSites: DirectoryProcessingSite[] | null;
  certificateChangeNote: string | null;
  pendingTermination: boolean;
  terminationDocStatus: string | null;
  terminationTrackingNote: string | null;
  paperReportArchived: boolean | null;
  verificationGroupNote: string | null;
  clientNote: string | null;
  adminGroupNote: string | null;
  // 狀態
  status: DirectoryStatus;
  paymentWarning: boolean;
  // TAP 專屬
  industryType: string | null;
  planNo: string | null;
  l1Uploaded: boolean | null;
  tapCode: string | null;
  labelUsage: string | null;
  productCategory: string | null;
  productItems: string | null;
  // TOP 專屬
  verifiedItems: string | null;
  environmentalSubsidyMonth: string | null;
}

interface ListResponse {
  items: DirectoryItem[];
  total: number;
  page: number;
  pageSize: number;
}

/** 列表過濾參數（與後端 directoryListQuerySchema 對應） */
export interface DirectoryListFilter {
  page?: number;
  pageSize?: number;
  category?: DirectoryCategory;
  status?: DirectoryStatus;
  search?: string;
  validUntilBefore?: string;
  paymentWarning?: boolean;
  // 表頭篩選（Excel autofilter 式）
  rotationWarning?: boolean;
  pendingTermination?: boolean;
  verificationType?: string;
  organicStage?: string;
  currentStatus?: string;
  registrationNo?: string;
  certificateNo?: string;
  clientName?: string;
  legalRepresentative?: string;
  counselor?: string;
}

export function useDirectoryList(filter: DirectoryListFilter) {
  return useQuery({
    queryKey: ['certified-org-directory', filter],
    queryFn: () => {
      const sp = new URLSearchParams();
      // 統一組裝：undefined / 空字串跳過
      (Object.entries(filter) as Array<[string, string | number | boolean | undefined]>).forEach(
        ([k, v]) => {
          if (v !== undefined && v !== '') sp.set(k, String(v));
        },
      );
      return apiClient.get<ListResponse>(`/api/certified-org-directory?${sp.toString()}`);
    },
  });
}

export function useDirectoryItem(id: number | null) {
  return useQuery({
    queryKey: ['certified-org-directory', 'item', id],
    queryFn: () => apiClient.get<{ item: DirectoryItem }>(`/api/certified-org-directory/${id}`),
    enabled: id !== null,
  });
}
